import React, { useMemo, useState } from 'react';
import { AlertOctagon, AlertTriangle, CheckCircle, ArrowUpDown } from 'lucide-react';
import { deriveInventorySummaryFromItems } from '../kpiUtils';

const INVENTORY_TABS = [
  { id: 'reagent', label: 'Reagents' },
  { id: 'hardware', label: 'EC Components' },
  { id: 'chemical', label: 'Raw Chemicals' },
];

const CATEGORY_LABEL = {
  reagent: 'Reagent',
  hardware: 'EC Component',
  chemical: 'Raw Chemical',
};

const EMPTY_MESSAGES = {
  reagent: 'No reagent inventory data available.',
  hardware: 'No EC component inventory data available.',
  chemical: 'No raw chemical inventory data available.',
};

const isBelowMin = (item) =>
  Number(item.current_stock || 0) <= Number(item.minimum_threshold || 0);

const sortInventoryItems = (items, sortDir) =>
  [...items].sort((a, b) => {
    const aLow = isBelowMin(a);
    const bLow = isBelowMin(b);
    if (aLow !== bLow) return aLow ? -1 : 1;
    if (aLow && bLow) {
      return Number(a.current_stock || 0) - Number(b.current_stock || 0);
    }
    const da = a.days_remaining ?? 999;
    const db = b.days_remaining ?? 999;
    return sortDir === 'asc' ? da - db : db - da;
  });

const DaysBar = ({ days, max = 14 }) => {
  const pct = days >= 999 ? 100 : Math.min((days / max) * 100, 100);
  const color = days <= 2 ? 'bg-red-500' : days <= 7 ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-14 h-1.5 rounded-full bg-slate-200 overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-semibold text-slate-700 tabular-nums">
        {days >= 999 ? 'OK' : `${days}d`}
      </span>
    </div>
  );
};

const ROW_ACCENT = {
  critical: 'border-l-red-500 bg-red-50/40',
  warning:  'border-l-amber-500 bg-amber-50/40',
  healthy:  'border-l-emerald-500 bg-white',
  stockout: 'border-l-red-700 bg-red-50/60',
  neutral:  'border-l-slate-300 bg-white',
};

const StatusBadge = ({ status }) => {
  const labels = { critical: 'Critical', warning: 'Warning', healthy: 'Healthy', stockout: 'Stockout' };
  const styles = {
    critical: 'bg-red-100 text-red-800 ring-1 ring-red-200',
    warning:  'bg-amber-100 text-amber-800 ring-1 ring-amber-200',
    healthy:  'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200',
    stockout: 'bg-red-200 text-red-900 ring-1 ring-red-300',
  };
  return (
    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${styles[status] || 'bg-slate-100 text-slate-700'}`}>
      {labels[status] || status}
    </span>
  );
};

const buildStockoutCalendar = (items) => {
  const calendar = {};
  const today = new Date();
  for (let i = 0; i < 7; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    const key = d.toISOString().split('T')[0];
    calendar[key] = [];
  }
  items.forEach((item) => {
    if (item.days_remaining < 999 && item.days_remaining >= 0) {
      const stockoutDate = new Date(today);
      stockoutDate.setDate(today.getDate() + item.days_remaining);
      const key = stockoutDate.toISOString().split('T')[0];
      if (key in calendar) calendar[key].push(item.name);
    }
  });
  return Object.entries(calendar).map(([date, items]) => ({ date, items }));
};

export default function InventoryHealthRadar({ data }) {
  const [sortDir, setSortDir] = useState('asc');
  const [inventoryTab, setInventoryTab] = useState('reagent');

  const itemsByTab = useMemo(
    () => ({
      reagent: data?.reagentInventoryItems || [],
      hardware: data?.ecInventoryItems || [],
      chemical: data?.chemicalInventoryItems || [],
    }),
    [data?.reagentInventoryItems, data?.ecInventoryItems, data?.chemicalInventoryItems]
  );

  const inventoryItems = itemsByTab[inventoryTab] || [];
  const sortedItems = useMemo(
    () => sortInventoryItems(inventoryItems, sortDir),
    [inventoryItems, sortDir]
  );
  const { criticalCount, warningCount, healthyCount } = useMemo(
    () => deriveInventorySummaryFromItems(inventoryItems),
    [inventoryItems]
  );
  const lowStockCount = useMemo(
    () => inventoryItems.filter(isBelowMin).length,
    [inventoryItems]
  );
  const calendar = useMemo(
    () => buildStockoutCalendar(inventoryItems),
    [inventoryItems]
  );

  if (!data) return null;

  const { stats } = data;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm h-full">
      <div className="flex flex-col gap-3 px-5 py-4 border-b border-slate-200 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-base font-semibold text-slate-800">Inventory Health</h3>
        <div className="flex flex-wrap gap-1.5">
          {INVENTORY_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setInventoryTab(tab.id)}
              className={`text-xs px-3 py-1.5 rounded-full font-medium transition-all ${
                inventoryTab === tab.id
                  ? 'bg-slate-800 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-3 gap-3 p-4 border-b border-slate-100">
        <div className="flex flex-col items-center gap-1 p-2 rounded-lg bg-red-50">
          <AlertOctagon className="w-4 h-4 text-red-500" />
          <p className="text-xl font-bold text-red-700 tabular-nums">{criticalCount}</p>
          <p className="text-[10px] font-medium uppercase tracking-wide text-red-500">Critical</p>
        </div>
        <div className="flex flex-col items-center gap-1 p-2 rounded-lg bg-amber-50">
          <AlertTriangle className="w-4 h-4 text-amber-500" />
          <p className="text-xl font-bold text-amber-700 tabular-nums">{warningCount}</p>
          <p className="text-[10px] font-medium uppercase tracking-wide text-amber-500">Warning</p>
        </div>
        <div className="flex flex-col items-center gap-1 p-2 rounded-lg bg-emerald-50">
          <CheckCircle className="w-4 h-4 text-emerald-500" />
          <p className="text-xl font-bold text-emerald-700 tabular-nums">{healthyCount}</p>
          <p className="text-[10px] font-medium uppercase tracking-wide text-emerald-500">Healthy</p>
        </div>
      </div>

      {lowStockCount > 0 ? (
        <div className="flex items-center gap-2 px-4 py-2 text-sm text-red-700 border-b border-red-100 bg-red-50/50">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span>
            {lowStockCount} item{lowStockCount === 1 ? '' : 's'} at or below minimum
          </span>
        </div>
      ) : null}

      {inventoryItems.length > 0 ? (
        <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-slate-50">
              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-600 border-b border-slate-200">
                <th className="px-4 py-2.5 font-semibold">Item</th>
                <th className="px-3 py-2.5 font-semibold">Stock</th>
                <th className="px-3 py-2.5 font-semibold">
                  <button
                    type="button"
                    className="flex items-center gap-1 text-slate-600 hover:text-slate-900"
                    onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
                  >
                    Days Left <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
                <th className="px-3 py-2.5 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.map((item) => {
                const low = isBelowMin(item);
                const accent = low ? '' : ROW_ACCENT[item.status] || ROW_ACCENT.neutral;
                return (
                  <tr
                    key={item.id}
                    className={`border-b border-slate-100 last:border-0 border-l-4 transition-colors ${
                      low
                        ? 'bg-red-50 hover:bg-red-100/60 border-l-red-400'
                        : `hover:bg-slate-50/80 ${accent}`
                    }`}
                  >
                    <td className="px-4 py-3">
                      <p className="font-semibold text-slate-900 leading-snug">{item.name}</p>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        {CATEGORY_LABEL[item.category] || item.category}
                      </p>
                    </td>
                    <td
                      className={`px-3 py-3 font-medium tabular-nums whitespace-nowrap ${
                        low ? 'text-red-700' : 'text-slate-800'
                      }`}
                    >
                      {item.current_stock} <span className="text-slate-500 font-normal">{item.unit}</span>
                    </td>
                    <td className="px-3 py-3"><DaysBar days={item.days_remaining ?? 999} /></td>
                    <td className="px-3 py-3"><StatusBadge status={item.status} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="p-4 text-sm text-slate-500">
          {inventoryTab === 'reagent' && stats.totalReagents > 0
            ? `${stats.totalReagents} reagents tracked · ${stats.lowStockItems} below minimum threshold`
            : EMPTY_MESSAGES[inventoryTab]}
        </div>
      )}

      {inventoryItems.length > 0 && inventoryTab !== 'chemical' && (
        <div className="px-4 py-3 border-t border-slate-100">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-600 mb-2">Predicted Stockouts — Next 7 Days</p>
          <div className="grid grid-cols-7 gap-1">
            {calendar.map(({ date, items: dayItems }) => {
              const label = new Date(date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric' });
              return (
                <div key={date} className="flex flex-col items-center gap-1">
                  <span className="text-[10px] font-medium text-slate-600">{label}</span>
                  {dayItems.length > 0 ? (
                    <span className="w-5 h-5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center" title={dayItems.join(', ')}>
                      {dayItems.length}
                    </span>
                  ) : (
                    <span className="w-5 h-5 rounded-full bg-slate-100 text-[9px] text-slate-300 flex items-center justify-center">·</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
